import {
  projectActionReceiptData,
  projectCanonicalActionReceipt,
  readActionReceiptDisposition,
} from "@/lib/actionRequests/actionReceipt";
import {
  isHumanToolCallResolved,
  isRealHumanToolCallPart,
  resolveAssistantStatusAfterStream,
} from "@/lib/humanToolCallUtils";
import {
  applyThreadHumanToolCallDedupe,
  stripNonAssistantStatus,
  type HumanToolCallCanonicalOrigin,
} from "@/lib/messageUtils";
import type { CNSEntityHolder } from "@/models/cns";
import type { ExtendedThreadMessageLike } from "@/types/message";
import {
  getToolNameForActionType,
  getToolNameForEntitiesType,
} from "@/utils/toolNameMapping";

type ToolCallPart = {
  type: "tool-call";
  toolCallId: string;
  toolName: string;
  argsText?: string;
  args?: unknown;
  state?: "call" | "result";
  result?: unknown;
  isError?: boolean;
  partId?: string;
};

const NATIVE_HUMAN_ACTION_MATERIALIZATION = "native_human_action_v1";
const WORKFLOW_ACTION_REQUEST_SLOT = "workflow";

function recordOrUndefined(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

function projectHistoricalPersistedActionReceipt(
  actionType: string,
  data: Readonly<Record<string, unknown>>,
  disposition: unknown,
  result: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const projected: Record<string, unknown> = {
    ...projectActionReceiptData(data),
    actionType: actionType.trim(),
    disposition,
  };
  for (const field of ["summary", "status", "completedAt"] as const) {
    const value = result[field];
    if (typeof value === "string" && value.trim().length > 0) {
      projected[field] = value;
    }
  }
  return projected;
}

/**
 * Native workflow-surface holders are durable action state, not transcript content.
 * Historical holders without this explicit contract marker retain the legacy append repair.
 */
export function isNativeWorkflowSurfaceHolder(
  entity: CNSEntityHolder,
): boolean {
  if (entity.assistantMessagePublicId || entity.streamPartId) return false;

  if (!isNativeActionRequestHolder(entity)) return false;

  const entityDataInvoke = recordOrUndefined(
    recordOrUndefined(entity.entityData)?.toolInvoke,
  );
  const actionRequestSlot =
    entity.toolInvoke?.actionRequestSlot ?? entityDataInvoke?.actionRequestSlot;

  return actionRequestSlot === WORKFLOW_ACTION_REQUEST_SLOT;
}

/** Native Action Requests keep their canonical presentation payload in the assistant message. */
function isNativeActionRequestHolder(entity: CNSEntityHolder): boolean {
  const entityDataInvoke = recordOrUndefined(
    recordOrUndefined(entity.entityData)?.toolInvoke,
  );
  const materializationContract =
    entity.toolInvoke?.materializationContract ??
    entityDataInvoke?.materializationContract;
  return materializationContract === NATIVE_HUMAN_ACTION_MATERIALIZATION;
}

export type ToolCallHydrationIssue =
  | {
      kind: "missing-origin-message";
      toolCallId: string;
      originMessagePublicId: string;
    }
  | {
      kind: "missing-origin-part";
      toolCallId: string;
      originMessagePublicId: string;
      streamPartId?: string;
    };

export type ToolCallHydrationResult = {
  messages: ExtendedThreadMessageLike[];
  issues: ToolCallHydrationIssue[];
};

function findUnambiguousLegacyPartIdRepairs(
  messages: ExtendedThreadMessageLike[],
  entitiesByToolCallId: ReadonlyMap<string, CNSEntityHolder>,
): ReadonlyMap<string, string> {
  const repairs = new Map<string, string>();
  for (const [toolCallId, entity] of entitiesByToolCallId) {
    const originMessagePublicId = entity.assistantMessagePublicId;
    const streamPartId = entity.streamPartId;
    if (!originMessagePublicId || !streamPartId) continue;

    const matchingParts = messages.flatMap((message) => {
      if (
        message.role !== "assistant" ||
        message.id !== originMessagePublicId ||
        !Array.isArray(message.content)
      ) {
        return [];
      }
      return message.content.filter(
        (part) =>
          isRealHumanToolCallPart(part) && part.toolCallId === toolCallId,
      );
    });
    if (matchingParts.length !== 1 || matchingParts[0]?.partId != null) {
      continue;
    }
    repairs.set(`${originMessagePublicId}:${toolCallId}`, streamPartId);
  }
  return repairs;
}

function authoritativeToolResult(entity: CNSEntityHolder): unknown {
  const toolResult = entity.toolInvoke?.toolResult;
  if (toolResult == null) return undefined;
  const resultRecord = recordOrUndefined(toolResult);
  if (
    resultRecord &&
    "toolData" in resultRecord &&
    resultRecord.toolData !== undefined
  ) {
    const toolData = resultRecord.toolData;
    const dataRecord = recordOrUndefined(toolData);
    if (!dataRecord || !isNativeActionRequestHolder(entity)) return toolData;

    const entityDataInvoke = recordOrUndefined(
      recordOrUndefined(entity.entityData)?.toolInvoke,
    );
    const actionType =
      entity.toolInvoke?.actionType ??
      (typeof entityDataInvoke?.actionType === "string"
        ? entityDataInvoke.actionType
        : undefined) ??
      (typeof resultRecord.actionType === "string"
        ? resultRecord.actionType
        : undefined);
    if (typeof actionType !== "string" || actionType.trim().length === 0) {
      return toolData;
    }
    const summary =
      typeof resultRecord.summary === "string" &&
      resultRecord.summary.trim().length > 0
        ? resultRecord.summary
        : undefined;
    const status =
      typeof resultRecord.status === "string" &&
      resultRecord.status.trim().length > 0
        ? resultRecord.status
        : undefined;
    const completedAt =
      typeof resultRecord.completedAt === "string" &&
      resultRecord.completedAt.trim().length > 0
        ? resultRecord.completedAt
        : undefined;
    const hasPersistedDisposition = Object.hasOwn(
      dataRecord,
      "receiptDisposition",
    );
    const persistedDisposition = dataRecord.receiptDisposition;
    const disposition = readActionReceiptDisposition({
      ...dataRecord,
      ...(hasPersistedDisposition
        ? { disposition: persistedDisposition }
        : {}),
      status,
    });
    // Only complete canonical ToolResults with a recognized terminal contract enter the current
    // projection. Historical or malformed persisted results keep their server fields but are
    // projected through the bounded compatibility path without inventing canonical metadata.
    if (!summary || !status || !completedAt || disposition === undefined) {
      return projectHistoricalPersistedActionReceipt(
        actionType,
        dataRecord,
        disposition ?? persistedDisposition,
        resultRecord,
      );
    }
    return projectCanonicalActionReceipt({
      actionType,
      disposition,
      data: dataRecord,
      summary,
      status,
      completedAt,
    });
  }
  return toolResult;
}

function legacyEntityCollection(entity: CNSEntityHolder): Record<string, unknown> {
  const entityData =
    entity.entityData && typeof entity.entityData === "object"
      ? (entity.entityData as Record<string, unknown>)
      : {};
  return {
    ...entityData,
    type:
      typeof entityData.type === "string"
        ? entityData.type
        : entity.entityType,
    ...(Array.isArray(entityData.items)
      ? {}
      : { items: entityData.items ?? [] }),
    toolInvoke: {
      ...recordOrUndefined(entityData.toolInvoke),
      ...entity.toolInvoke,
    },
    ...(entity.toolInvoke?.toolResult != null
      ? { toolResult: entity.toolInvoke.toolResult }
      : {}),
  };
}

/** Compatibility-only projection for holders persisted before message-part provenance was required. */
function buildLegacyToolCallPartFromEntity(
  entity: CNSEntityHolder,
): ToolCallPart | null {
  const invoke = entity.toolInvoke;
  const toolCallId = invoke?.toolCallId;
  if (!toolCallId) return null;

  const result = authoritativeToolResult(entity);
  if (!invoke.requiresAction && result === undefined) return null;

  const toolName = invoke.actionType
    ? getToolNameForActionType(invoke.actionType)
    : getToolNameForEntitiesType(entity.entityType);
  if (!toolName || toolName === "unknown_tool") return null;

  const args = {
    entities: [legacyEntityCollection(entity)],
  };

  return {
    type: "tool-call",
    toolCallId,
    toolName,
    argsText: JSON.stringify(args),
    args,
    state: result === undefined ? "call" : "result",
    ...(entity.streamPartId ? { partId: entity.streamPartId } : {}),
    ...(result === undefined ? {} : { result }),
  };
}

function mergeAuthoritativeEntity(
  part: ToolCallPart,
  entity: CNSEntityHolder,
): ToolCallPart {
  const legacyProjection = buildLegacyToolCallPartFromEntity(entity);
  const result = authoritativeToolResult(entity);

  if (result !== undefined) {
    const preserveStreamedArgs = part.args != null;
    return {
      ...part,
      ...(legacyProjection && !preserveStreamedArgs
        ? {
            toolName: legacyProjection.toolName,
            argsText: legacyProjection.argsText,
            args: legacyProjection.args,
          }
        : {}),
      state: "result",
      result,
      isError: false,
    };
  }

  if (entity.toolInvoke?.requiresAction) {
    // Human-action results are terminal. Independent REST reads can observe an older open holder
    // before a later raw-message reread sees the completed part; never regress that durable result.
    if (isHumanToolCallResolved(part.result, part.isError)) {
      return part;
    }
    const unresolvedPart = { ...part };
    delete unresolvedPart.result;
    delete unresolvedPart.isError;
    if (isNativeActionRequestHolder(entity)) {
      // The durable assistant message owns the validated card payload and its exact stream
      // position. The holder owns lifecycle and result state; its REST summary must not rebuild
      // or narrow the already canonical arguments.
      return {
        ...unresolvedPart,
        state: "call",
      };
    }
    return {
      ...unresolvedPart,
      ...(legacyProjection
        ? {
            toolName: legacyProjection.toolName,
            argsText: legacyProjection.argsText,
            args: legacyProjection.args,
          }
        : {}),
      state: "call",
    };
  }

  return part;
}

/**
 * Reconciles thread tool-call parts with authoritative entity-holder toolInvoke data.
 *
 * Existing parts receive persisted holder state. A provenanced holder whose raw part is absent is
 * reported for an authoritative Message reread because identities are not display ordinals.
 * Holders without provenance use the last assistant message for backward compatibility.
 */
export function reconcileEntityBackedToolCalls(
  messages: ExtendedThreadMessageLike[],
  entities: CNSEntityHolder[],
): ToolCallHydrationResult {
  const entitiesByToolCallId = new Map(
    entities
      .filter((entity) => entity.toolInvoke?.toolCallId)
      .map((entity) => [entity.toolInvoke!.toolCallId, entity]),
  );
  const canonicalOriginsByToolCallId = new Map<
    string,
    HumanToolCallCanonicalOrigin
  >();
  for (const entity of entities) {
    const toolCallId = entity.toolInvoke?.toolCallId;
    if (!toolCallId || !entity.assistantMessagePublicId) continue;
    canonicalOriginsByToolCallId.set(toolCallId, {
      messageId: entity.assistantMessagePublicId,
      ...(entity.streamPartId ? { partId: entity.streamPartId } : {}),
    });
  }

  const seenToolCallIds = new Set<string>();
  const matchedCanonicalToolCallIds = new Set<string>();
  const legacyLatestToolCallIds = new Set(
    entities
      .filter(
        (entity) =>
          entity.toolInvoke?.toolCallId &&
          !entity.assistantMessagePublicId &&
          !isNativeWorkflowSurfaceHolder(entity),
      )
      .map((entity) => entity.toolInvoke!.toolCallId),
  );
  const legacyPartIdRepairs = findUnambiguousLegacyPartIdRepairs(
    messages,
    entitiesByToolCallId,
  );

  // Merge holder state into raw durable positions before selecting one canonical instance.
  let reconciled = messages.map((message) => {
    if (!Array.isArray(message.content)) return message;
    let changed = false;
    const content = message.content.map((part) => {
      if (
        !isRealHumanToolCallPart(
          part as {
            type: string;
            toolName?: string;
            toolCallId?: string;
          },
        )
      ) {
        return part;
      }
      seenToolCallIds.add(part.toolCallId);
      const entity = entitiesByToolCallId.get(part.toolCallId);
      if (!entity) return part;
      if (
        entity.assistantMessagePublicId &&
        (message.role !== "assistant" ||
          entity.assistantMessagePublicId !== message.id)
      ) {
        return part;
      }
      const originKey = `${message.id ?? ""}:${part.toolCallId}`;
      const repairedPartId = legacyPartIdRepairs.get(originKey);
      const identifiedPart =
        part.partId == null && repairedPartId
          ? { ...part, partId: repairedPartId }
          : part;
      if (
        entity.streamPartId &&
        entity.streamPartId !== identifiedPart.partId
      ) {
        return part;
      }
      if (entity.assistantMessagePublicId) {
        matchedCanonicalToolCallIds.add(part.toolCallId);
      }
      const merged = mergeAuthoritativeEntity(
        identifiedPart as unknown as ToolCallPart,
        entity,
      );
      changed = changed || merged !== part;
      return merged;
    });
    if (!changed) return message;
    if (message.role !== "assistant") {
      return stripNonAssistantStatus({ ...message, content });
    }
    return stripNonAssistantStatus({
      ...message,
      content,
      status: resolveAssistantStatusAfterStream(content),
    }) as ExtendedThreadMessageLike;
  }) as ExtendedThreadMessageLike[];

  let legacyTargetIndex = -1;
  for (let index = reconciled.length - 1; index >= 0; index -= 1) {
    if (reconciled[index].role === "assistant") {
      legacyTargetIndex = index;
      break;
    }
  }

  const additionsByMessageIndex = new Map<number, ToolCallPart[]>();
  const assignedToolCallIds = new Set(seenToolCallIds);
  const issues: ToolCallHydrationIssue[] = [];
  for (const entity of entities) {
    const toolCallId = entity.toolInvoke?.toolCallId;
    if (!toolCallId) continue;
    if (isNativeWorkflowSurfaceHolder(entity)) continue;
    const part = buildLegacyToolCallPartFromEntity(entity);
    if (!part) continue;

    if (entity.assistantMessagePublicId) {
      if (matchedCanonicalToolCallIds.has(toolCallId)) continue;
      const originExists = reconciled.some(
        (message) =>
          message.role === "assistant" &&
          message.id === entity.assistantMessagePublicId,
      );
      if (!originExists) {
        issues.push({
          kind: "missing-origin-message",
          toolCallId,
          originMessagePublicId: entity.assistantMessagePublicId,
        });
      } else {
        // Message/part identities locate a card but do not encode its content ordinal.
        issues.push({
          kind: "missing-origin-part",
          toolCallId,
          originMessagePublicId: entity.assistantMessagePublicId,
          ...(entity.streamPartId
            ? { streamPartId: entity.streamPartId }
            : {}),
        });
      }
      continue;
    }

    if (assignedToolCallIds.has(toolCallId) || legacyTargetIndex < 0) continue;

    additionsByMessageIndex.set(legacyTargetIndex, [
      ...(additionsByMessageIndex.get(legacyTargetIndex) ?? []),
      part,
    ]);
    assignedToolCallIds.add(toolCallId);
  }

  if (additionsByMessageIndex.size > 0) {
    reconciled = reconciled.map((message, index) => {
      const missingParts = additionsByMessageIndex.get(index);
      if (!missingParts) return message;
      const content = Array.isArray(message.content) ? [...message.content] : [];
      const nextContent = [...content, ...missingParts];
      return stripNonAssistantStatus({
        ...message,
        content: nextContent,
        status: resolveAssistantStatusAfterStream(nextContent),
      }) as ExtendedThreadMessageLike;
    });
  }

  return {
    messages: applyThreadHumanToolCallDedupe(reconciled, {
      canonicalOriginsByToolCallId,
      legacyLatestToolCallIds,
      recomputeAssistantStatus: true,
    }),
    issues,
  };
}

/** @deprecated Prefer reconcileEntityBackedToolCalls for new code. */
export const hydrateMissingToolCallsFromEntitiesWithReport =
  reconcileEntityBackedToolCalls;

/** Compatibility projection for consumers that do not need recovery diagnostics. */
export function hydrateMissingToolCallsFromEntities(
  messages: ExtendedThreadMessageLike[],
  entities: CNSEntityHolder[],
): ExtendedThreadMessageLike[] {
  return reconcileEntityBackedToolCalls(messages, entities).messages;
}
